import { supabase } from '../../infrastructure/supabase/supabaseClient';
import { PublicDoctorProfile } from '../../domain/types/profile.types';
import { decode } from 'base64-arraybuffer';
import { DirectoryService } from './DirectoryService';

export interface OnboardingData {
  rol_nombre: string;
  titulo_principal: string;
  resumen_profesional: string;
  atiende_virtual: boolean;
  atiende_presencial: boolean;
  atiende_domicilio: boolean;
  estado_id?: number | null;
  ciudad_id?: number | null;
  direccion_fisica?: string | null;
  latitud?: number | null;
  longitud?: number | null;
  avatar_url?: string | null;
  licencia_profesional?: string | null; // Dinámico según rol
  licencia_mpps?: string | null;
  licencia_fmv?: string | null;
  licencia_fpv?: string | null;
  licencia_cmv?: string | null;
  licencia_runsai?: string | null;
  especialidad_id?: number | null;
}

export class ProfessionalProfileService {

  static async getEspecialidades() {
    const { data, error } = await supabase
      .from('vw_especialidades_disponibles')
      .select('*')
      .order('nombre_especialidad', { ascending: true });
      
    if (error) {
      console.error('Error fetching especialidades:', error);
      return [];
    }
    return data || [];
  }

  static async getClinicalRoles() {
    const { data, error } = await supabase
      .from('roles_polimorficos_maestros')
      .select('rol_nombre')
      .eq('es_clinico', true)
      .neq('rol_nombre', 'PROFESIONAL_CLINICO')
      .order('rol_nombre', { ascending: true });
      
    if (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
    return data?.map(r => r.rol_nombre) || [];
  }

  /**
   * Obtiene el perfil público completo de un profesional por su UUID,
   * incluyendo identidad, especialidades, geografía y bóveda legal.
   */
  static async getPublicProfileById(profesionalId: string): Promise<PublicDoctorProfile | null> {
    const { data, error } = await supabase
      .from('perfiles_profesionales')
      .select(`
        profesional_uuid,
        titulo_principal,
        resumen_profesional,
        atiende_virtual,
        atiende_presencial,
        atiende_domicilio,
        atiende_domicilio,
        direccion_consultorio,
        verificado_global,
        pm_banco,
        pm_telefono,
        pm_cedula,
        perfiles_usuario!inner(
          nombres_completos, 
          apellidos_completos, 
          avatar_url,
          estado_id,
          ciudad_id,
          latitud,
          longitud,
          usuario_roles_polimorficos(
            roles_polimorficos_maestros(rol_nombre)
          )
        ),
        geografia_ciudades(ciudad_nombre, geografia_estados(nombre_estado)),
        profesionales_salud(mpps_registro, fmv_nro, fpv_nro),
        profesionales_veterinaria(cmv_nro, runsai_nro),
        profesional_especialidades(
          especialidades_maestras(especialidad_id, nombre_especialidad)
        )
      `)
      .eq('profesional_uuid', profesionalId)
      .maybeSingle();

    if (error) {
      console.error('[ProfileService] Error fetching public profile:', error);
      return null;
    }

    if (!data) {
      // Si no existe el perfil profesional todavía, rescatamos los datos básicos de identidad
      const { data: userData, error: userError } = await supabase
        .from('perfiles_usuario')
        .select(`
          nombres_completos,
          apellidos_completos,
          avatar_url,
          estado_id,
          ciudad_id,
          latitud,
          longitud
        `)
        .eq('perfil_usuario_id', profesionalId)
        .maybeSingle();

      if (userError || !userData) return null;

      return {
        id: profesionalId,
        nombres_completos: userData.nombres_completos,
        apellidos_completos: userData.apellidos_completos,
        avatar_url: userData.avatar_url,
        estado_id: userData.estado_id,
        ciudad_id: userData.ciudad_id,
        latitud: userData.latitud,
        longitud: userData.longitud,
      } as unknown as PublicDoctorProfile;
    }

    const u = parseArrayOrObject(data.perfiles_usuario);
    const c = parseArrayOrObject(data.geografia_ciudades);
    const s = parseArrayOrObject(data.profesionales_salud);
    const v = parseArrayOrObject(data.profesionales_veterinaria);
    const esp = data.profesional_especialidades;

    return {
      id: data.profesional_uuid,
      nombres_completos: u?.nombres_completos || '',
      apellidos_completos: u?.apellidos_completos || '',
      avatar_url: u?.avatar_url || null,
      estado_id: u?.estado_id || null,
      ciudad_id: u?.ciudad_id || null,
      latitud: u?.latitud || null,
      longitud: u?.longitud || null,
      rol_nombre: extractRolNombre(data.perfiles_usuario),
      titulo_principal: data.titulo_principal,
      resumen_profesional: data.resumen_profesional,
      atiende_virtual: data.atiende_virtual,
      atiende_presencial: data.atiende_presencial,
      atiende_domicilio: data.atiende_domicilio,
      direccion_consultorio: data.direccion_consultorio,
      ciudad_nombre: c?.ciudad_nombre || null,
      estado_nombre: extractEstadoNombre(data.geografia_ciudades),
      verificado_global: data.verificado_global,
      licencia_mpps: s?.mpps_registro || null,
      licencia_fmv: s?.fmv_nro || null,
      licencia_fpv: s?.fpv_nro || null,
      licencia_cmv: v?.cmv_nro || null,
      licencia_runsai: v?.runsai_nro || null,
      especialidad_id: extractEspecialidadId(esp),
      especialidades: extractEspecialidades(esp),
      pm_banco: data.pm_banco || null,
      pm_telefono: data.pm_telefono || null,
      pm_cedula: data.pm_cedula || null
    };
  }

  /**
   * Envía los datos del onboarding a la base de datos para crear el perfil
   * público del profesional y asignarle el rol clínico correspondiente.
   */
  static async submitOnboarding(data: OnboardingData): Promise<boolean> {
    const { error } = await supabase.rpc('fn_completar_onboarding_profesional', {
      p_rol_nombre: data.rol_nombre,
      p_titulo_principal: data.titulo_principal,
      p_resumen_profesional: data.resumen_profesional,
      p_atiende_virtual: data.atiende_virtual,
      p_atiende_presencial: data.atiende_presencial,
      p_atiende_domicilio: data.atiende_domicilio,
      p_estado_id: data.estado_id || null,
      p_ciudad_id: data.ciudad_id || null,
      p_direccion_fisica: data.direccion_fisica || null,
      p_latitud: data.latitud || null,
      p_longitud: data.longitud || null,
      p_especialidad_id: data.especialidad_id || null
    });

    if (error) {
      console.error('Error en Onboarding:', error);
      throw new Error(error.message || 'No se pudo completar el onboarding. Intenta de nuevo.');
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;

    if (userId) {
      if (data.avatar_url) {
        await supabase.from('perfiles_usuario').update({ avatar_url: data.avatar_url }).eq('perfil_usuario_id', userId);
      }
      if (data.titulo_principal) {
        await upsertLicencias(userId, data.titulo_principal, data.licencia_mpps, data.licencia_fmv, data.licencia_fpv, data.licencia_cmv, (data as any).licencia_runsai);
      }
    }

    // Invalidar caché del motor de búsqueda para mostrar los datos frescos
    DirectoryService.clearEngine();

    return true;
  }

  /**
   * Actualiza los datos del perfil público del profesional.
   */
  static async updateProfile(data: Partial<PublicDoctorProfile>): Promise<boolean> {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    if (!userId) throw new Error('No authenticado');

    const updatePayload = buildUpdatePayload(data);

    if (Object.keys(updatePayload).length > 0) {
      const { error } = await supabase
        .from('perfiles_profesionales')
        .update(updatePayload)
        .eq('profesional_uuid', userId);
      
      if (error) {
        console.error('[ProfileService] Update Error:', error);
        throw error;
      }
    }

    await updateUsuarioPayload(userId, data);

    // Licencias Profesionales
    if (data.titulo_principal) {
      await upsertLicencias(userId, data.titulo_principal, data.licencia_mpps, data.licencia_fmv, data.licencia_fpv, data.licencia_cmv, (data as any).licencia_runsai);
    }

    await updateEspecialidad(userId, data.especialidad_id);
    
    // Limpiar caché
    DirectoryService.clearEngine();

    return true;
  }

  /**
   * Sube una imagen base64 al Bucket de Supabase y retorna la URL pública.
   */
  static async uploadAvatar(base64Image: string, _oldExtensionIgnored?: string): Promise<string> {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    if (!userId) throw new Error('No authenticado');

    // 1. Detectar el formato real de la imagen usando la firma Base64 (Magic Bytes)
    // Los archivos PNG en base64 siempre empiezan con 'iVBORw0K'
    const isPng = base64Image.startsWith('iVBORw0K');
    const extension = isPng ? 'png' : 'jpg';
    const contentType = isPng ? 'image/png' : 'image/jpeg';
    
    // 2. Limpiar CUALQUIER avatar anterior explícitamente para evitar acumulación
    // No importa si antes era PNG y ahora JPG, borramos ambos posibles formatos viejos
    await supabase.storage.from('avatars').remove([
      `${userId}_avatar.jpg`, 
      `${userId}_avatar.jpeg`, 
      `${userId}_avatar.png`
    ]);

    // Usamos SIEMPRE un nombre estático y limpio en la raíz
    const fileName = `${userId}_avatar.${extension}`;
    
    // Subir el archivo al bucket "avatars"
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, decode(base64Image), {
        contentType: contentType,
        upsert: true,
      });

    if (uploadError) {
      console.error('[ProfileService] Error subiendo avatar:', uploadError);
      throw new Error('No se pudo subir la foto de perfil.');
    }

    // Obtener la URL pública
    const { data: publicUrlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    // Añadimos un cache-buster a la URL para que la app (React Native) recargue la imagen nueva, 
    // pero en el servidor de Supabase sigue habiendo 1 solo archivo.
    const publicUrl = `${publicUrlData.publicUrl}?t=${Date.now()}`;

    // Actualizar inmediatamente en la tabla perfiles_usuario
    const { error: updateError } = await supabase
      .from('perfiles_usuario')
      .update({ avatar_url: publicUrl })
      .eq('perfil_usuario_id', userId);

    if (updateError) {
      console.error('[ProfileService] Error actualizando avatar_url en BD:', updateError);
      throw new Error('No se pudo guardar la foto de perfil en la base de datos.');
    }

    return publicUrl;
  }
}

function parseArrayOrObject<T>(val: T | T[] | undefined | null): T | null {
  if (!val) return null;
  return Array.isArray(val) ? val[0] : val;
}

function extractRolNombre(perfilesUsuario: any): string {
  const u = parseArrayOrObject(perfilesUsuario);
  if (!u?.usuario_roles_polimorficos || u.usuario_roles_polimorficos.length === 0) {
    return 'MEDICO';
  }
  const rolObj = u.usuario_roles_polimorficos[0];
  if (!rolObj?.roles_polimorficos_maestros) {
    return 'MEDICO';
  }
  const maestro = parseArrayOrObject(rolObj.roles_polimorficos_maestros);
  return maestro?.rol_nombre || 'MEDICO';
}

function extractEstadoNombre(geografiaCiudades: any): string | null {
  const c = parseArrayOrObject(geografiaCiudades);
  if (!c?.geografia_estados) return null;
  const estado = parseArrayOrObject(c.geografia_estados);
  return estado?.nombre_estado || null;
}

function extractEspecialidadId(especialidades: any[] | undefined | null): number | null {
  if (!especialidades || especialidades.length === 0) return null;
  const first = especialidades[0];
  if (!first?.especialidades_maestras) return null;
  const maestra = parseArrayOrObject(first.especialidades_maestras);
  return maestra?.especialidad_id || null;
}

function extractEspecialidades(especialidades: any[] | undefined | null): string[] {
  if (!especialidades) return [];
  return especialidades
    .map((e: any) => {
      if (!e?.especialidades_maestras) return null;
      const maestra = parseArrayOrObject(e.especialidades_maestras);
      return maestra?.nombre_especialidad;
    })
    .filter((n): n is string => typeof n === 'string');
}

async function upsertLicencias(userId: string, tituloPrincipal: string, mpps?: string | null, fmv?: string | null, fpv?: string | null, cmv?: string | null, runsai?: string | null): Promise<void> {
  const isVete = tituloPrincipal.toUpperCase() === 'VETERINARIO';
  
  if (isVete) {
    if (cmv) {
      const payload: any = { profesional_uuid: userId, cmv_nro: cmv, runsai_nro: runsai || null };
      await supabase.from('profesionales_veterinaria').upsert(payload);
    }
  } else if (mpps || fmv || fpv) {
    const payload: any = { 
      profesional_uuid: userId, 
      mpps_registro: mpps || null,
      fmv_nro: fmv || null,
      fpv_nro: fpv || null
    };
    await supabase.from('profesionales_salud').upsert(payload);
  }
}

async function updateUsuarioPayload(userId: string, data: Partial<PublicDoctorProfile>) {
  const usuarioPayload: any = {};
  if (data.avatar_url !== undefined) usuarioPayload.avatar_url = data.avatar_url;
  if (data.estado_id !== undefined) usuarioPayload.estado_id = data.estado_id;
  if (data.ciudad_id !== undefined) usuarioPayload.ciudad_id = data.ciudad_id;
  if (data.latitud !== undefined) usuarioPayload.latitud = data.latitud;
  if (data.longitud !== undefined) usuarioPayload.longitud = data.longitud;
  if (data.direccion_consultorio !== undefined) usuarioPayload.perfil_direccion_fisica = data.direccion_consultorio;

  if (Object.keys(usuarioPayload).length > 0) {
    const { error: geoError } = await supabase
      .from('perfiles_usuario')
      .update(usuarioPayload)
      .eq('perfil_usuario_id', userId);
      
    if (geoError) {
      console.error('[ProfileService] Error actualizando datos de usuario/geolocalización:', geoError);
      throw new Error('No se pudo actualizar la información de usuario o geolocalización.');
    }
  }
}

async function updateEspecialidad(userId: string, especialidadId?: number | null) {
  if (especialidadId === undefined) return;

  const { error: delError } = await supabase
    .from('profesional_especialidades')
    .delete()
    .eq('profesional_uuid', userId);
    
  if (delError) {
    console.error('[ProfileService] Error limpiando especialidades:', delError);
    throw new Error('No se pudo actualizar la especialidad.');
  }

  if (especialidadId !== null) {
    const { error: insError } = await supabase
      .from('profesional_especialidades')
      .insert({ profesional_uuid: userId, especialidad_id: especialidadId });
      
    if (insError) {
      console.error('[ProfileService] Error insertando especialidad:', insError);
      throw new Error('No se pudo guardar la especialidad seleccionada.');
    }
  }
}

function buildUpdatePayload(data: Partial<PublicDoctorProfile>): any {
  const updatePayload: any = {};
  if (data.titulo_principal !== undefined) updatePayload.titulo_principal = data.titulo_principal;
  if (data.resumen_profesional !== undefined) updatePayload.resumen_profesional = data.resumen_profesional;
  if (data.atiende_virtual !== undefined) updatePayload.atiende_virtual = data.atiende_virtual;
  if (data.atiende_presencial !== undefined) updatePayload.atiende_presencial = data.atiende_presencial;
  if (data.atiende_domicilio !== undefined) updatePayload.atiende_domicilio = data.atiende_domicilio;
  if (data.direccion_consultorio !== undefined) updatePayload.direccion_consultorio = data.direccion_consultorio;
  if (data.pm_banco !== undefined) updatePayload.pm_banco = data.pm_banco;
  if (data.pm_telefono !== undefined) updatePayload.pm_telefono = data.pm_telefono;
  if (data.pm_cedula !== undefined) updatePayload.pm_cedula = data.pm_cedula;
  return updatePayload;
}
